import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    Typography,
    Box,
    Button
} from '@mui/material';
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { postData, getData, putData } from '../../../webService/webservice.js'
import "../../../styles/Auth/purchase.css";
import { DateTime } from "luxon";
import plusIcon from '@iconify/icons-mdi/eye-circle-outline';
import editIcon from '@iconify/icons-mdi/square-edit-outline';
import { useNavigate, useLocation } from 'react-router-dom';
import PurchasePaymentLogsPopup from "./viewPurchasePaymentPopUp.jsx";
const PurchasePaymentPage = ({ filters }) => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);
    const [purchases, setPurchases] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isPaymentPopup, setIsPaymentPopup] = useState(false);
    const [isPurchasePaymentLogPopupOpen, setIsPurchasePaymentLogPopupOpen] = useState(false);
    const [selectedPurchasePaymentLogs, setSelectedPurchasePaymentLogs] = useState(null);
    const [loading, setLoading] = useState(false);
    const sessionToken = sessionStorage.getItem("sessionToken");
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [limit, setLimit] = useState(5);
    const commonCellStyle = {
        color: '#525252',
        fontSize: '12px',
        fontWeight: 600,
        textAlign: 'center'
    };
    const headings = [
        "SL",
        "Purchase Invoice Date",
        "Product Name",
        "Supplier",
        "Purchase Invoice Number",
        "Purchase Amount",
        "Paid Amount",
        "Due Amount",
        "Payment Status",
        "Action",
    ];
    const fetchPurchase = async (page, limit, searchQuery, filters = {}) => {
        const params = {
            page,
            limit,
            supplierName: searchQuery?.trim() || "",
        };
        if (filters.startDate) params.startDate = filters.startDate;
        if (filters.endDate) params.endDate = filters.endDate;
        try {
            setLoading(true);
            const response = await getData('purchases/purchases', params, sessionToken);
            const resultsWrapper = response?.data?.[0];
            const purchaseList = resultsWrapper?.paginatedResults || [];
            const totalCount = resultsWrapper?.totalCount || 0;
            const totalPages = resultsWrapper?.totalPages || 1;

            setPurchases(purchaseList);
            setTotalCount(totalCount);
            setTotalPages(totalPages);
        } catch (error) {
            toast.error(error.message || "Failed to fetch Purchases");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchase(currentPage, limit, searchQuery, filters);
    }, [currentPage, limit, refreshKey, searchQuery, filters]);

    useEffect(() => {
        const fetchSupplier = async () => {
            try {
                const token = sessionStorage.getItem("sessionToken");
                const data = await getData('suppliers/suppliers', {}, token);
                console.log("API Full Response: ", data);
                const results = data?.data?.results || [];
                setSuppliers(results);
            } catch (error) {
                console.error("Failed to fetch Suppliers", error);
            }
        }
        fetchSupplier();
    }, []);

    const [paymentForm, setPaymentForm] = useState({
        purchaseId: "",
        paymentAmount: "",
        paymentMode: "",
        referenceNo: "",
        remarks: ""
    });

    const handleAddPurchasePayment = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (!paymentForm.paymentAmount || !paymentForm.paymentMode) {
            alert("Payment amount and mode are required");
            return;
        }
        try {
            const payload = {
                purchaseId: selectedPurchase?._id, // comes from table click
                paymentAmount: Number(paymentForm.paymentAmount),
                paymentMode: paymentForm.paymentMode,
                referenceNo: paymentForm.referenceNo,
                remarks: paymentForm.remarks,
            };
            const response = await postData("purchases/purchasepayment", payload, sessionToken);
            toast.success(response.message || "Purchase Payment added successfully");
            setIsPaymentPopup(false);
            setPaymentForm({
                purchaseId: "",
                paymentAmount: "",
                paymentMode: "",
                referenceNo: "",
                remarks: "",
            });
            navigate("/purchaseinvoice");
            fetchPurchase(currentPage, limit, searchQuery, filters);
        } catch (error) {
            console.error("Error adding payment:", error);
            alert("Error adding payment");
        }
    };

    const handleViewPurchasePaymentLog = async (purchaseId) => {
        try {
            setLoading(true);
            const sessionToken = sessionStorage.getItem("sessionToken");

            const response = await getData(
                `purchases/purchasepaymentslog/${purchaseId}`,
                sessionToken
            );

            // ✅ Pass the full response (no .data here)
            setSelectedPurchasePaymentLogs(response);
            setIsPurchasePaymentLogPopupOpen(true);
        } catch (error) {
            console.error("Error fetching Purchase Payment Logs:", error);
            toast.error(error.message || "Failed to fetch Payment Logs data");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (direction) => {
        setCurrentPage((prev) =>
            direction === "next" ? Math.min(prev + 1, totalPages) : Math.max(prev - 1, 1)
        );
    };
    const handleItemsPerPageChange = (e) => {
        const value = e.target.value;
        const newLimit = value === "all" ? "all" : Number(value);
        setLimit(newLimit);
        setCurrentPage(1);
    };

    const handleExportExcel = () => {
        if (purchases.length === 0) {
            toast.error("No data to export");
            return;
        }
        const exportData = purchases.map((p, i) => {
            const supplier = p.supplierDetails?.[0];
            const product = p.productDetails?.[0];

            return {
                SL: i + 1,
                Purchase_Invoice_Date: p.purchaseInvoiceDate
                    ? DateTime.fromISO(p.purchaseInvoiceDate).toFormat("dd LLL yyyy")
                    : "—",
                Product_Name: product?.title || "—",
                Supplier_Name: supplier?.supplierName || "—",
                Invoice_Number: p.purchaseInvoiceNumber || "—",
                Purchase_Amount: p.purchaseInvoiceAmount || 0,
                Paid_Amount: p.paidAmount || 0,
                Due_Amount: p.dueAmount || 0,
                Payment_Status: p.paymentStatus || "—",
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Purchase_Payments");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const file = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(file, "purchase-invoices.xlsx");
    };

    return (
        <div className="purchase-layout1">
            <div className="purchase-content1">
                <main className="purchase-main1">

                    <>
                        <div className="Purchase-widgets">
                            <div className="Purchase-widgets-header">
                                <h3>Purchases Invoices</h3>
                                {/* <input
                                    type="text"
                                    placeholder="Search"
                                    className="Purchase-search-input"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                /> */}
                            </div>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleExportExcel}
                                    sx={{ textTransform: "none", borderRadius: "8px" }}
                                    style={{background:"rgb(141, 38, 41)"}}
                                >
                                    Export Excel
                                </Button>
                            </Box>
                        </div>
                        <TableContainer component={Paper} sx={{
                            borderRadius: '12px',
                            maxHeight: 400,
                            overflowY: "auto",
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                        }}>
                            <Table className="custom-product-table"
                                sx={{
                                    '& td, & th': {
                                        border: 'none',
                                    },
                                    border: 'none',
                                }}>
                                <TableHead className="custom-product-head">
                                    <TableRow>
                                        {headings.map((heading, index) => (
                                            <TableCell
                                                key={index}
                                                sx={{
                                                    border: 'none',
                                                    padding: '12px 16px',
                                                    textAlign: 'center',
                                                    position: "sticky",
                                                    top: 0,
                                                    backgroundColor: '#E6CF80',
                                                    zIndex: 1,

                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '4px',
                                                        color: '#000000',
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {heading}
                                                </Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {purchases.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={11}>
                                                <Typography
                                                    sx={{
                                                        textAlign: "center",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        padding: "20px",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    No purchase payments found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        purchases.map((purchasePayment, index) => {
                                            const purchaseInfo = purchasePayment; // ✅ main purchase data
                                            const supplierInfo = purchasePayment.supplierDetails?.[0];
                                            const productInfo = purchasePayment.productDetails?.[0];

                                            return (
                                                <TableRow key={purchasePayment._id}>
                                                    {/* SL */}
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>{index + 1}</Typography>
                                                    </TableCell>

                                                    {/* Purchase Invoice Date */}
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>
                                                            {purchaseInfo?.purchaseInvoiceDate
                                                                ? DateTime.fromISO(purchaseInfo.purchaseInvoiceDate).toFormat("dd LLL yyyy")
                                                                : "—"}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Product Name */}
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>
                                                            {productInfo?.title || "—"}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Supplier */}
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>
                                                            {supplierInfo?.supplierName || "—"}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Purchase Invoice Number */}
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>
                                                            {purchaseInfo?.purchaseInvoiceNumber || "—"}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Purchase Amount */}
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>
                                                            ₹{purchaseInfo?.purchaseInvoiceAmount?.toLocaleString() || 0}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Paid Amount */}
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>
                                                            ₹{purchaseInfo?.paidAmount?.toLocaleString() || 0}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Due Amount */}
                                                    <TableCell>
                                                        <Typography sx={commonCellStyle}>
                                                            ₹{purchaseInfo?.dueAmount?.toLocaleString() || 0}
                                                        </Typography>
                                                    </TableCell>
                                                    {/* Payment Status */}
                                                    <TableCell>
                                                        <Typography
                                                            sx={{
                                                                ...commonCellStyle,
                                                                color:
                                                                    purchaseInfo?.paymentStatus === "Paid"
                                                                        ? "green"
                                                                        : purchaseInfo?.paymentStatus === "Due"
                                                                            ? "orange"
                                                                            : "red",
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {purchaseInfo?.paymentStatus || "—"}
                                                        </Typography>
                                                    </TableCell>

                                                    {/* Actions */}
                                                    <TableCell>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                gap: 2,
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <Icon icon={plusIcon} width={20} height={20} onClick={() => handleViewPurchasePaymentLog(purchasePayment._id)} />
                                                            <PurchasePaymentLogsPopup
                                                                isOpen={isPurchasePaymentLogPopupOpen}
                                                                onClose={() => setIsPurchasePaymentLogPopupOpen(false)}
                                                                purchaseLogsData={selectedPurchasePaymentLogs}
                                                            />
                                                            <Icon
                                                                icon={editIcon}
                                                                width={20}
                                                                height={20}

                                                                onClick={() => {
                                                                    setSelectedPurchase(purchasePayment);
                                                                    setIsPaymentPopup(true);
                                                                }}
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <div className="purchase-bottom">
                            <div className="purchase-section show-entries">
                                <span className="text-label">Show</span>
                                <select className="entries-select" value={limit}
                                    onChange={handleItemsPerPageChange}
                                >
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="all">All</option>
                                </select>
                                <span className="text-label">Entries</span>
                            </div>
                            <div className="purchase-section showing-range">
                                {
                                    limit === 'all'
                                        ? `Showing all ${totalCount} entries`
                                        : `Showing ${(currentPage - 1) * limit + 1} to ${Math.min(currentPage * limit, totalCount)} of ${totalCount} entries`
                                }
                            </div>
                            <div className="purchase-section spacer"></div>
                            <div className="purchase-section pagination-buttons">
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange("prev")}
                                >Previous</button>
                                <button
                                    className="pagination-btn"
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange("next")}
                                >Next</button>
                            </div>
                        </div>
                        {isPaymentPopup && (
                            <div className="popup-overlay">
                                <div className="popup-wrapper">

                                    <div className="popup-form">
                                        <h2 className="popup-title">Add Purchase Payment</h2>

                                        <form
                                            className="popup-form-body"
                                            onSubmit={handleAddPurchasePayment}
                                        >
                                            {/* Row 1: Payment Amount + Payment Mode */}
                                            <div className="form-group-row">
                                                <div className="form-group">
                                                    <label htmlFor="paymentAmount">Payment Amount</label>
                                                    <input
                                                        type="number"
                                                        id="paymentAmount"
                                                        className="purchase-input"
                                                        required
                                                        placeholder="Enter payment amount"
                                                        value={paymentForm.paymentAmount}
                                                        onChange={(e) =>
                                                            setPaymentForm((prev) => ({
                                                                ...prev,
                                                                paymentAmount: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                </div>

                                                <div className="form-group">
                                                    <label htmlFor="paymentMode">Payment Mode</label>
                                                    <select
                                                        id="paymentMode"
                                                        required
                                                        className="purchase-input"
                                                        value={paymentForm.paymentMode}
                                                        onChange={(e) =>
                                                            setPaymentForm((prev) => ({
                                                                ...prev,
                                                                paymentMode: e.target.value,
                                                            }))
                                                        }
                                                    >
                                                        <option value="">Select Mode</option>
                                                        <option value="Cash">Cash</option>
                                                        <option value="Card">Card</option>
                                                        <option value="UPI">UPI</option>
                                                        <option value="Bank Transfer">Bank Transfer</option>
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Row 2: Reference No + Remarks */}
                                            <div className="form-group-row">
                                                <div className="form-group">
                                                    <label htmlFor="referenceNo">Reference No</label>
                                                    <input
                                                        type="text"
                                                        id="referenceNo"
                                                        placeholder="Enter Reference Number"
                                                        value={paymentForm.referenceNo}
                                                        onChange={(e) =>
                                                            setPaymentForm((prev) => ({
                                                                ...prev,
                                                                referenceNo: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                </div>

                                                <div className="form-group">
                                                    <label htmlFor="remarks">Remarks</label>
                                                    <input
                                                        type="text"
                                                        id="remarks"
                                                        placeholder="Enter remarks"
                                                        value={paymentForm.remarks}
                                                        onChange={(e) =>
                                                            setPaymentForm((prev) => ({
                                                                ...prev,
                                                                remarks: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                </div>
                                            </div>

                                            {/* Buttons */}
                                            <div className="form-actions">
                                                <button type="submit" className="btn-primary">
                                                    Save Payment
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    onClick={() => setIsPaymentPopup(false)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        )}

                    </>
                </main >
            </div >
        </div >
    );

}

export default PurchasePaymentPage;